import React from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { Coins, Sparkles, ArrowRight, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

// Logo URL
const LOGO_URL = "/logo.jpeg";

export function WelcomeScreen({ onStart, userName }) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const toggleLanguage = () => {
    const newLang = (currentLang || 'en').startsWith('en') ? 'sk' : 'en';
    i18n.changeLanguage(newLang);
  };
  const motivationalMessages = [
    { icon: '🎯', text: t('welcome.card1', 'Challenge yourself daily!') },
    { icon: '💰', text: t('welcome.card2', 'Earn real money rewards!') },
    { icon: '🏆', text: t('welcome.card3', 'Compete on leaderboards!') },
    { icon: '🧠', text: t('welcome.card4', 'Expand your knowledge!') },
  ];

  // Use portal to render outside normal DOM hierarchy
  return ReactDOM.createPortal(
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a1628] flex items-center justify-center p-4"
      style={{ zIndex: 9999, margin: 0 }}
    >
      {/* Language Switcher - top right, same as internal pages */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleLanguage}
        className="absolute top-4 right-4 flex items-center space-x-2 px-3 py-2 rounded-lg bg-slate-800/60 hover:bg-slate-700/80 text-teal-400 transition-colors border border-slate-600/50 backdrop-blur-sm z-10"
        data-testid="welcome-language-switcher"
        title={currentLang === 'en' ? 'Switch to Slovak' : 'Prepnúť na angličtinu'}
      >
        <Globe className="w-4 h-4" />
        <span className="text-sm font-semibold uppercase">{(currentLang || 'en').startsWith('en') ? 'EN' : 'SK'}</span>
      </motion.button>

      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px]" />
      </div>

      <motion.div 
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="relative max-w-lg w-full text-center"
      >
        {/* Logo */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
          className="w-24 h-24 mx-auto mb-6 rounded-full overflow-hidden border-4 border-teal-400/50 shadow-xl shadow-teal-500/30"
        >
          <img src={LOGO_URL} alt="SocraQuest" className="w-full h-full object-cover" />
        </motion.div>

        {/* Greeting */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h1 className="text-3xl font-bold text-white mb-2">
            {t('welcome.hello', 'Hello')}{userName ? `, ${userName}` : ''}! 👋
          </h1>
          <p className="text-xl text-teal-400 font-medium mb-6">
            {t('welcome.ready', 'Ready to challenge yourself today?')}
          </p>
        </motion.div>

        {/* Motivational Cards */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-2 gap-3 mb-8"
        >
          {motivationalMessages.map((msg, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + idx * 0.1 }}
              className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-3 flex items-center gap-2"
            >
              <span className="text-2xl">{msg.icon}</span>
              <span className="text-sm text-slate-300 text-left">{msg.text}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Reward Highlight */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9 }}
          className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4 mb-8"
        >
          <div className="flex items-center justify-center gap-3">
            <Coins className="w-8 h-8 text-yellow-400" />
            <div className="text-left">
              <p className="text-yellow-400 font-bold text-lg">
                {t('welcome.moneyTitle', 'Earn Real Money!')}
              </p>
              <p className="text-yellow-300/70 text-sm">
                {t('welcome.moneyText', 'Top players win monthly prizes up to €100')}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Start Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
        >
          <Button
            onClick={onStart}
            className="w-full py-6 text-lg font-bold bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 shadow-xl shadow-teal-500/30 rounded-xl"
            data-testid="start-playing-btn"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {t('welcome.start', 'Start Playing')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <p className="text-slate-500 text-sm mt-4">
            {t('welcome.waiting', '10 daily assessments waiting for you!')}
          </p>
        </motion.div>
      </motion.div>
    </motion.div>,
    document.body
  );
}
